import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { BsClipboardCheck, BsClockHistory } from "react-icons/bs";
import { MdInventory2 } from "react-icons/md";

const ManagerDashboard = () => {
  const { token, user } = useAuthStore();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [recentReqs, setRecentReqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pendingRes, recentRes] = await Promise.all([
          fetch("/api/getAllRequisitions?status=pending_manager_approval&limit=1", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch("/api/getAllRequisitions?limit=5", {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const pendingData = await pendingRes.json();
        const recentData = await recentRes.json();

        if (pendingRes.ok) setPendingCount(pendingData.totalCount || 0);
        if (recentRes.ok) setRecentReqs(recentData.requisitions || []);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const statusColor = {
    pending_manager_approval: "bg-orange-100 text-orange-700",
    pending_owner_approval: "bg-yellow-100 text-yellow-700",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
    fulfilled: "bg-emerald-100 text-emerald-700",
    draft: "bg-gray-100 text-gray-600",
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">
          Welcome, {user?.name}
        </h2>
        <p className="text-sm text-gray-500 mt-1">Manager Dashboard</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <div
          className="bg-white rounded-xl shadow p-5 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-orange-400"
          onClick={() => navigate("/Requisitions?status=pending_manager_approval")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Awaiting Your Approval</p>
              <p className="text-3xl font-bold text-orange-500 mt-1">
                {loading ? "—" : pendingCount}
              </p>
            </div>
            <BsClipboardCheck className="text-4xl text-orange-300" />
          </div>
          <p className="text-xs text-gray-400 mt-3">Click to review pending requisitions</p>
        </div>

        <div
          className="bg-white rounded-xl shadow p-5 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-blue-400"
          onClick={() => navigate("/Requisitions")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">All Requisitions</p>
              <p className="text-3xl font-bold text-blue-500 mt-1">
                <BsClockHistory className="inline" />
              </p>
            </div>
            <BsClipboardCheck className="text-4xl text-blue-300" />
          </div>
          <p className="text-xs text-gray-400 mt-3">View full company requisition history</p>
        </div>

        <div
          className="bg-white rounded-xl shadow p-5 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-indigo-400"
          onClick={() => navigate("/StockMaster")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Stock Catalog</p>
              <p className="text-xs text-gray-400 mt-2">Browse inventory items</p>
            </div>
            <MdInventory2 className="text-4xl text-indigo-300" />
          </div>
        </div>
      </div>

      {/* Recent Requisitions */}
      <div className="bg-white rounded-xl shadow p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-600 uppercase">Recent Requisitions</h3>
          <button
            onClick={() => navigate("/Requisitions")}
            className="text-xs text-primary hover:underline"
          >
            View All
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : recentReqs.length === 0 ? (
          <p className="text-sm text-gray-400">No requisitions found.</p>
        ) : (
          <div className="space-y-3">
            {recentReqs.map((req) => (
              <div
                key={req._id}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 px-2 rounded"
                onClick={() => navigate(`/Requisitions/View/${req._id}`)}
              >
                <div>
                  <p className="text-sm font-medium text-primary">{req.requisitionNumber}</p>
                  <p className="text-xs text-gray-400">{req.requestedBy?.name || "N/A"}</p>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    statusColor[req.status] || "bg-gray-100 text-gray-600"
                  }`}
                >
                  {req.status?.replace(/_/g, " ")}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerDashboard;
